import React, { useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import styled from 'styled-components';

const MarkdownContainer = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${props => props.sent 
    ? props.theme.colors.userMessageText 
    : props.theme.colors.botMessageText};
  
  & > *:first-child {
    margin-top: 0;
  }
  
  & > *:last-child {
    margin-bottom: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
  }

  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.2em; }
  h4 { font-size: 1.1em; }
  h5 { font-size: 1em; }
  h6 { font-size: 1em; }

  p {
    margin-bottom: 16px;
  }

  ul, ol {
    margin-top: 8px;
    margin-bottom: 16px;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }

  a {
    color: ${props => props.sent 
      ? props.theme.colors.userMessageText 
      : props.theme.colors.primary};
    text-decoration: underline;
  }

  code {
    padding: 2px 4px;
    background: ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.2)' 
      : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 3px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
    font-size: 85%;
  }

  pre {
    margin: 8px 0;
    padding: 16px;
    overflow: auto;
    border-radius: ${props => props.theme.borderRadius.message};
    background: ${props => props.sent 
      ? 'rgba(0, 0, 0, 0.2)' 
      : '#1e1e1e'};
  }

  pre code {
    background: none;
    padding: 0;
    white-space: pre;
  }

  blockquote {
    margin: 0 0 16px;
    padding: 0 16px;
    color: ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.9)' 
      : props.theme.colors.secondaryText};
    border-left: 4px solid ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.3)' 
      : props.theme.colors.border};
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
  }

  th, td {
    padding: 6px 12px;
    border: 1px solid ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.2)' 
      : props.theme.colors.border};
  }

  th {
    background: ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.1)' 
      : props.theme.colors.secondaryBackground};
  }

  img {
    max-width: 100%;
    border-radius: 4px;
  }

  hr {
    height: 1px;
    background-color: ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.2)' 
      : props.theme.colors.border};
    border: none;
    margin: 16px 0;
  }
`;

const MessageSeparator = styled.hr`
  height: 1px;
  background-color: ${props => props.sent 
    ? 'rgba(255, 255, 255, 0.2)' 
    : props.theme.colors.border};
  border: none;
  margin: 16px 0;
`;

const ImageContainer = styled.div`
  margin: 12px 0;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    display: block;
    cursor: pointer;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const StyledLink = styled.a`
  display: inline-block;
  padding: 8px 16px;
  margin: 4px 0;
  background: ${props => props.sent 
    ? 'rgba(255, 255, 255, 0.2)' 
    : props.theme.colors.primary};
  color: ${props => props.sent 
    ? props.theme.colors.userMessageText 
    : '#ffffff'} !important;
  border-radius: 6px;
  text-decoration: none !important;
  font-weight: 500;
  cursor: pointer !important;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.sent 
    ? 'rgba(255, 255, 255, 0.3)' 
    : props.theme.colors.primary};
  pointer-events: auto !important;
  user-select: none;
  
  /* Style cho button element */
  &[type="button"] {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    text-align: left;
    width: auto;
  }
  
  &:hover {
    background: ${props => props.sent 
      ? 'rgba(255, 255, 255, 0.3)' 
      : props.theme.colors.primaryDark || '#0056b3'};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Component riêng cho link để có thể dùng hooks
const ClickableLink = ({ href, sent, children, handleLinkClick, ...props }) => {
  const handledRef = React.useRef(false);
  
  const handlePointerDown = (e) => {
    // Chỉ xử lý left click (button 0 or -1 for touch)
    if (e.button > 0) return;
    
    // Ngăn xử lý nhiều lần
    if (handledRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    handledRef.current = true;
    e.preventDefault();
    e.stopPropagation();
    
    // Gọi handleLinkClick
    handleLinkClick(e, href);
    
    // Reset sau 1s
    setTimeout(() => {
      handledRef.current = false;
    }, 1000);
  };
  
  const handleClick = (e) => {
    // Chặn onClick hoàn toàn vì đã xử lý trong onPointerDown
    e.preventDefault();
    e.stopPropagation();
  };
  
  return (
    <StyledLink 
      as="button"
      type="button"
      href={href}
      sent={sent}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    >
      {children}
    </StyledLink>
  );
};

const MarkdownMessage = ({ content, sent, onImageClick }) => {
  // Handler để mở link - gửi event để tránh bị chặn popup
  // Sử dụng useCallback để tránh tạo function mới mỗi lần render
  const handleLinkClick = useCallback((e, href) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Gửi event lên window để App.js xử lý
    const customEvent = new CustomEvent('openLink', { 
      detail: { url: href },
      bubbles: true,
      cancelable: true
    });
    
    window.dispatchEvent(customEvent);
  }, []); // Empty deps - function không thay đổi

  // Handler để mở modal ảnh
  const handleImageClick = useCallback((src) => {
    if (onImageClick) {
      onImageClick(src);
    }
  }, [onImageClick]);
  // Phát hiện URL hình ảnh trong text
  const detectImageUrl = (text) => {
    const imageUrlPattern = /(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|bmp|webp|svg))/gi;
    return text.match(imageUrlPattern);
  };

  // Xử lý và render một đoạn text
  const renderTextSegment = (text) => {
    // Loại bỏ markdown image syntax ![alt](url) và chỉ giữ lại URL
    const markdownImagePattern = /!\[([^\]]*)\]\((https?:\/\/[^\)]+\.(?:jpg|jpeg|png|gif|bmp|webp|svg))\)/gi;
    const processedText = text.replace(markdownImagePattern, '$2');
    
    const imageUrls = detectImageUrl(processedText);
    
    if (!imageUrls || imageUrls.length === 0) {
      // Không có ảnh, render markdown bình thường
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  style={vscDarkPlus}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
            img({ node, src, alt, ...props }) {
              return (
                <ImageContainer>
                  <img 
                    src={src} 
                    alt={alt || 'Image'}
                    onClick={() => handleImageClick(src)}
                    {...props}
                  />
                </ImageContainer>
              );
            },
            a({ node, href, children, ...props }) {
              return (
                <ClickableLink
                  href={href}
                  sent={sent}
                  handleLinkClick={handleLinkClick}
                  {...props}
                >
                  {children}
                </ClickableLink>
              );
            }
          }}
        >
          {processedText}
        </ReactMarkdown>
      );
    }

    // Có ảnh, tách text và ảnh
    const parts = [];
    let remainingText = processedText;
    
    imageUrls.forEach((url, index) => {
      const [beforeImage, afterImage] = remainingText.split(url);
      
      if (beforeImage.trim()) {
        parts.push({ type: 'text', content: beforeImage.trim(), key: `text-${index}` });
      }
      
      parts.push({ type: 'image', content: url, key: `image-${index}` });
      remainingText = afterImage || '';
    });
    
    if (remainingText.trim()) {
      parts.push({ type: 'text', content: remainingText.trim(), key: `text-final` });
    }

    return parts.map((part) => {
      if (part.type === 'image') {
      return (
        <ImageContainer key={part.key}>
        <img 
          src={part.content} 
          onClick={() => handleImageClick(part.content)}
        />
        </ImageContainer>
      );
      }
      
      return (
        <div key={part.key}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              img({ node, src, alt, ...props }) {
                return (
                  <ImageContainer>
                    <img 
                      src={src} 
                      alt={alt || 'Image'}
                      onClick={() => handleImageClick(src)}
                      {...props}
                    />
                  </ImageContainer>
                );
              },
              a({ node, href, children, ...props }) {
                return (
                  <ClickableLink
                    href={href}
                    sent={sent}
                    handleLinkClick={handleLinkClick}
                    {...props}
                  >
                    {children}
                  </ClickableLink>
                );
              }
            }}
          >
            {part.content}
          </ReactMarkdown>
        </div>
      );
    });
  };

  // Tách nội dung theo dấu ---
  const segments = content.split(/\n?---\n?/).filter(seg => seg.trim());

  return (
    <MarkdownContainer sent={sent}>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {renderTextSegment(segment)}
          {index < segments.length - 1 && <MessageSeparator sent={sent} />}
        </React.Fragment>
      ))}
    </MarkdownContainer>
  );
};

export default MarkdownMessage;